import {
  abbreviateNumber,
  AnalyticIdentifier,
  centsToDollars,
  dollarsToCents,
  formatDollarsFromCents,
  Maybe,
} from '@common/neo.tax';
import { Alert, Badge, Checkbox, colors, CountryCell, Stack, Typography, useModal } from '@ui-components/neo.tax';
import { ReactComponent as CloudIcon } from '@ui-components/neo.tax/dist/icons/cloud.svg';
import { ReactComponent as ContractorIcon } from '@ui-components/neo.tax/dist/icons/contractors.svg';
import { ReactComponent as SupplyIcon } from '@ui-components/neo.tax/dist/icons/lab-tube-bottle.svg';
import { ReactComponent as LegalIcon } from '@ui-components/neo.tax/dist/icons/legal-scale-unequal.svg';
import { ReactComponent as OverheadIcon } from '@ui-components/neo.tax/dist/icons/overhead.svg';
import compact from 'lodash/compact';
import uniqueId from 'lodash/uniqueId';
import { ReactElement, ReactNode, useMemo, useState } from 'react';
import { CellProps, Column, type UseRowSelectInstanceProps } from 'react-table';

import { RD_CATEGORY_SINGULAR_LABELS } from '@neo.tax/rd/constants';
import { useRdContext } from '@neo.tax/rd/context';
import {
  useGetRdExpenseProjectAllocationReport,
  useRdPercentageBulkApply,
  useUploadRdExpenseProjectAllocationReport,
} from '@neo.tax/rd/hooks';
import { useAccountingQres, type AccountingQre } from '@neo.tax/rd/hooks/useAccountingQres';
import { AccountingQreSummary, useAccountingQreSummary } from '@neo.tax/rd/hooks/useAccountingQreSummary';
import { RdLockPageLayout } from '@neo.tax/rd/organisms/rd-lock-page-layout';
import {
  type ProviderUnion,
  type SyncedProviders,
  TABLE_CELL_PLACEHOLDER,
  SourcesContent,
  TableHeader,
  AccountingQreSortFields,
  ColumnDefinition,
  FilterSortDataType,
  getPath,
  Path,
  useCurrentCompanyId,
  useFilterConditions,
  useKeywordSearch,
  useSorting,
  getCountryCodeAlpha2,
  ACCOUNTING_PROVIDER_NAMES,
  RdCategory,
  SortDirection,
  ProviderType,
  useColumnVisibility,
  useCurrentUserScopeForCurrentCompany,
  PercentCell,
} from '@neo.tax/shared';
import {
  BulkAllocationModal,
  ExpenseLayout,
  FooterCell,
  NavLinkTo,
  TypographyFullWidth,
} from '@neo.tax/shared/organisms';
import { ApplyRDPercentageToExpensesButton } from '@neo.tax/shared/organisms/expense-layout/expense-layout.styled';

import { AccountingQreDetail } from '../accounting-qre-detail';
import { EditQreExpenseAllocationModalLoader } from '../qre-allocation/EditQreExpenseAllocationModalLoader';
import { useAccountingQreLayoutAnalyticIds } from './useAccountingQreLayoutAnalyticIds';

const RD_CATEGORY_BADGES: Record<RdCategory, ReactNode> = {
  [RdCategory.Contractor]: (
    <Badge colorVariant="green" icon={<ContractorIcon />}>
      {RD_CATEGORY_SINGULAR_LABELS[RdCategory.Contractor]}
    </Badge>
  ),
  [RdCategory.Cloud]: (
    <Badge colorVariant="green" icon={<CloudIcon />}>
      {RD_CATEGORY_SINGULAR_LABELS[RdCategory.Cloud]}
    </Badge>
  ),
  [RdCategory.Supply]: (
    <Badge colorVariant="green" icon={<SupplyIcon />}>
      {RD_CATEGORY_SINGULAR_LABELS[RdCategory.Supply]}
    </Badge>
  ),
  [RdCategory.Legal]: (
    <Badge colorVariant="green" icon={<LegalIcon />}>
      {RD_CATEGORY_SINGULAR_LABELS[RdCategory.Legal]}
    </Badge>
  ),
  [RdCategory.Overhead]: (
    <Badge colorVariant="clear" icon={<OverheadIcon />}>
      {RD_CATEGORY_SINGULAR_LABELS[RdCategory.Overhead]}
    </Badge>
  ),
  [RdCategory.NonRd]: (
    <Badge colorVariant="clear" dot={false}>
      {RD_CATEGORY_SINGULAR_LABELS[RdCategory.NonRd]}
    </Badge>
  ),
  // These aren't expected values for these QREs.
  [RdCategory.AccountingPayroll]: null,
  [RdCategory.Unknown]: null,
  [RdCategory.Employee]: null,
};

type AccountingQreCell = CellProps<AccountingQre, Maybe<string>> & UseRowSelectInstanceProps<AccountingQre>;

const accountingQreTableColumns =
  (
    footerData: AccountingQreSummary | null,
    limitAccessToFinancialData: boolean,
    modalOptions: {
      toggle: () => void;
      setSelectedRdExpenseId: (selectedRdExpenseId: string) => void;
    },
    visibleColumns: AccountingQreSortFields[],
  ) =>
  ({
    handleClickRow,
    handleSelectAllRows,
    handleSelectRow,
    rowCount,
    selectedRowIds,
    syncedProviders,
  }: {
    handleClickRow: (cell: AccountingQreCell) => () => void;
    handleSelectAllRows: (checked: boolean) => void;
    handleSelectRow: (cell: AccountingQreCell) => (checked: boolean) => void;
    rowCount: number;
    selectedRowIds: string[];
    syncedProviders?: SyncedProviders;
  }): Column<AccountingQre>[] => {
    const amountCentsColumns: Column<AccountingQre>[] = limitAccessToFinancialData
      ? []
      : [
          {
            id: AccountingQreSortFields.QualifiedAmountCents,
            accessor: 'qualifiedAmountCents',
            width: 145,
            Header: () => <TableHeader textAlign="right">Qualified $</TableHeader>,
            Cell: ({ value }: CellProps<AccountingQre, AccountingQre['qualifiedAmountCents']>): ReactElement => (
              <TypographyFullWidth as="p" color="inherit" fontSize="inherit" textAlign="right">
                {value != null ? formatDollarsFromCents(value, { showCents: false }) : TABLE_CELL_PLACEHOLDER}
              </TypographyFullWidth>
            ),
            Footer: (): ReactElement => {
              const hasData = (footerData?.sum?.qualifiedAmountCents ?? null) !== null;
              const abbreviatedValue = abbreviateNumber({
                inputNumber: centsToDollars(footerData?.sum?.qualifiedAmountCents ?? 0),
              }).abbreviation;
              const value = hasData ? `$${abbreviatedValue}` : '0';
              return <FooterCell preText="TOTAL" text={value} alignText="end" />;
            },
          },
          {
            id: AccountingQreSortFields.AmountCents,
            accessor: 'amountCents',
            width: 145,
            Header: () => <TableHeader textAlign="right">Amount $</TableHeader>,
            Cell: ({ value }: CellProps<AccountingQre, AccountingQre['amountCents']>): ReactElement => (
              <TypographyFullWidth as="p" color="inherit" fontSize="inherit" textAlign="right">
                {value != null ? formatDollarsFromCents(value, { showCents: false }) : TABLE_CELL_PLACEHOLDER}
              </TypographyFullWidth>
            ),
            Footer: (): ReactElement => {
              const hasData = (footerData?.sum?.amountCents ?? null) !== null;
              const abbreviatedValue = abbreviateNumber({
                inputNumber: centsToDollars(footerData?.sum?.amountCents ?? 0),
              }).abbreviation;
              const value = hasData ? `$${abbreviatedValue}` : '0';
              return <FooterCell preText="TOTAL" text={value} alignText="end" />;
            },
          },
        ];

    const columns: Column<AccountingQre>[] = [
      {
        id: AccountingQreSortFields.Name,
        accessor: 'name',
        width: 190,
        Header: () => (
          <Stack direction="row">
            <Checkbox
              id="expenseIds"
              name="expenseIds"
              checked={selectedRowIds.length > 0 && selectedRowIds.length === rowCount}
              onChange={handleSelectAllRows}
            />
            <TableHeader>Name</TableHeader>
          </Stack>
        ),
        Cell: (
          cell: CellProps<AccountingQre, AccountingQre['name']> & UseRowSelectInstanceProps<AccountingQre>,
        ): ReactElement => (
          <Stack direction="row">
            <Checkbox
              id={cell.row.original.id}
              name={cell.row.original.id}
              checked={selectedRowIds?.includes(cell.row.original.id)}
              onChange={handleSelectRow(cell)}
            />
            <Typography
              as="button"
              color={colors.primary[600]}
              fontWeight="medium"
              fontSize="inherit"
              lineHeight="inherit"
              textOverflow="truncate"
              title={cell.value ?? undefined}
              onClick={handleClickRow(cell)}
            >
              {cell.value}
            </Typography>
          </Stack>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.count?.name ?? '0';
          return <FooterCell preText="COUNT" text={value} />;
        },
      },
      {
        id: AccountingQreSortFields.Account,
        accessor: 'account',
        width: 190,
        Header: () => <TableHeader>Account</TableHeader>,
        Cell: ({ value }: CellProps<AccountingQre, AccountingQre['account']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.account ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: AccountingQreSortFields.Department,
        accessor: 'department',
        width: 190,
        Header: () => <TableHeader>Department</TableHeader>,
        Cell: ({ value }: CellProps<AccountingQre, AccountingQre['department']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.department ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: AccountingQreSortFields.CostCenter,
        accessor: 'costCenter',
        width: 190,
        Header: () => <TableHeader>Cost Center</TableHeader>,
        Cell: ({ value }: CellProps<AccountingQre, AccountingQre['costCenter']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.costCenter ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: AccountingQreSortFields.Entity,
        accessor: 'entity',
        width: 190,
        Header: () => <TableHeader>Entity</TableHeader>,
        Cell: ({ value }: CellProps<AccountingQre, AccountingQre['entity']>): ReactElement => (
          <Typography color="inherit" textOverflow="truncate" fontSize="inherit" title={value ?? undefined}>
            {value || TABLE_CELL_PLACEHOLDER}
          </Typography>
        ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.entity ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      ...amountCentsColumns,
      {
        id: AccountingQreSortFields.QreRdPercentage,
        accessor: 'qreRdPercentage',
        width: 105,
        Header: () => <TableHeader textAlign="right">R&D %</TableHeader>,
        Cell: ({ row, value }: CellProps<AccountingQre, AccountingQre['qreRdPercentage']>): ReactElement => (
          <PercentCell
            onClick={(): void => {
              modalOptions.setSelectedRdExpenseId(row.original.id);
              modalOptions.toggle();
            }}
            percent={value}
            isSelected={row.isSelected}
          />
        ),
        Footer: (): ReactElement => {
          const hasData = (footerData?.avg?.qreRdPercentage ?? null) !== null;
          const value = hasData ? `${Math.round(footerData?.avg?.qreRdPercentage ?? 0)}%` : '0';
          return <FooterCell preText="AVG" text={value} alignText="end" />;
        },
      },
      {
        id: AccountingQreSortFields.AccountingProvider,
        accessor: (datum: AccountingQre): ProviderUnion[] => compact([datum.accountingProvider]),
        Cell: ({ value }: CellProps<AccountingQre, ProviderUnion[]>): ReactElement => (
          <SourcesContent linkedProviders={value} allProviders={syncedProviders} placeholder={TABLE_CELL_PLACEHOLDER} />
        ),
        Header: () => <TableHeader>Sources</TableHeader>,
      },
      {
        id: AccountingQreSortFields.Country,
        accessor: (datum: AccountingQre): string | null | undefined => datum.country,
        width: 110,
        Header: () => <TableHeader>Country</TableHeader>,
        Cell: ({ value }: CellProps<AccountingQre, string | null | undefined>): ReactElement | string =>
          typeof value === 'string' && value.length ? (
            <CountryCell countryCode={getCountryCodeAlpha2(value)}>{value}</CountryCell>
          ) : (
            TABLE_CELL_PLACEHOLDER
          ),
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.country ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: AccountingQreSortFields.State,
        accessor: (datum: AccountingQre): string | null | undefined => datum.state,
        width: 110,
        Header: () => <TableHeader>US State</TableHeader>,
        Cell: ({ value }: CellProps<AccountingQre, string | null | undefined>): string =>
          value || TABLE_CELL_PLACEHOLDER,
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.state ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
      {
        id: AccountingQreSortFields.RdCategory,
        accessor: 'rdCategory',
        width: 'auto',
        Header: () => <TableHeader>R&D Category</TableHeader>,
        Cell: ({ value }: CellProps<AccountingQre, AccountingQre['rdCategory']>): ReactNode =>
          RD_CATEGORY_BADGES[value],
        Footer: (): ReactElement => {
          const value = footerData?.uniqueCount?.rdCategory ?? '0';
          return <FooterCell preText="UNIQUE" text={value} />;
        },
      },
    ];

    return columns.filter((column) => visibleColumns.includes(column.id as AccountingQreSortFields));
  };

const sortAndFilterableColumnsForLimitedAccessUser = [
  { name: AccountingQreSortFields.Name, label: 'Name', dataType: FilterSortDataType.TEXT },
  { name: AccountingQreSortFields.Account, label: 'Account', dataType: FilterSortDataType.TEXT },
  { name: AccountingQreSortFields.Department, label: 'Department', dataType: FilterSortDataType.TEXT },
  { name: AccountingQreSortFields.CostCenter, label: 'Cost Center', dataType: FilterSortDataType.TEXT },
  { name: AccountingQreSortFields.Entity, label: 'Entity', dataType: FilterSortDataType.TEXT },
  { name: AccountingQreSortFields.QreRdPercentage, label: 'R&D %', dataType: FilterSortDataType.NUMBER },
  {
    name: AccountingQreSortFields.AccountingProvider,
    label: 'Source',
    dataType: FilterSortDataType.ENUM,
    enum: Object.entries(ACCOUNTING_PROVIDER_NAMES).map(([value, label]) => ({ value, label })),
  },
  { name: AccountingQreSortFields.Country, label: 'Country', dataType: FilterSortDataType.TEXT },
  { name: AccountingQreSortFields.State, label: 'US State', dataType: FilterSortDataType.TEXT },
  {
    name: AccountingQreSortFields.RdCategory,
    label: 'R&D Category',
    dataType: FilterSortDataType.ENUM,
    enum: Object.entries(RD_CATEGORY_SINGULAR_LABELS).map(([value, label]) => ({ value, label })),
  },
] as const satisfies readonly ColumnDefinition<AccountingQreSortFields>[];

const indexOfEntityColumn = sortAndFilterableColumnsForLimitedAccessUser.findIndex(
  (column) => column.name === AccountingQreSortFields.Entity,
);
const sortAndFilterableColumnsForFullAccessUser = [
  ...sortAndFilterableColumnsForLimitedAccessUser.slice(0, indexOfEntityColumn + 1),
  {
    name: AccountingQreSortFields.QualifiedAmountCents,
    label: 'Qualified $',
    dataType: FilterSortDataType.NUMBER,
    toHuman: (n): number | null => (n === null ? null : centsToDollars(Number(n))),
    fromHuman: (n): number | null => (n === null ? null : dollarsToCents(Number(n))),
  },
  {
    name: AccountingQreSortFields.AmountCents,
    label: 'Amount $',
    dataType: FilterSortDataType.NUMBER,
    toHuman: (n): number | null => (n === null ? null : centsToDollars(Number(n))),
    fromHuman: (n): number | null => (n === null ? null : dollarsToCents(Number(n))),
  },
  ...sortAndFilterableColumnsForLimitedAccessUser.slice(indexOfEntityColumn + 1),
] as const satisfies readonly ColumnDefinition<AccountingQreSortFields>[];

export const AccountingQreLayout = (): ReactElement => {
  const { fiscalYear, fiscalYearId, isNowPastPeriodEndDate } = useRdContext();
  const { isOpen, setIsOpen, toggle } = useModal();
  const [selectedRdExpenseId, setSelectedRdExpenseId] = useState<string | undefined>(undefined);
  const { limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const analyticIds = useAccountingQreLayoutAnalyticIds();
  const searchKeywordState = useKeywordSearch();
  const { download: downloadBulkEditTemplate, loading: isDownloadingBulkEditTemplate } =
    useGetRdExpenseProjectAllocationReport(fiscalYearId, ProviderType.Accounting);
  const bulkEditUpload = useUploadRdExpenseProjectAllocationReport(fiscalYearId, ProviderType.Accounting);

  const sortAndFilterableColumns = limitAccessToFinancialData
    ? sortAndFilterableColumnsForLimitedAccessUser
    : sortAndFilterableColumnsForFullAccessUser;
  const initialSorting = [
    {
      id: uniqueId('AccountingQreSortField'),
      column: AccountingQreSortFields.QreRdPercentage,
      direction: SortDirection.Desc,
    },
  ];
  if (!limitAccessToFinancialData) {
    initialSorting.push({
      id: uniqueId('AccountingQreSortField'),
      column: AccountingQreSortFields.AmountCents,
      direction: SortDirection.Desc,
    });
  }

  const filterState = useFilterConditions({ columns: sortAndFilterableColumns, persistenceKey: 'rdAccountingQre' });
  const sortState = useSorting({
    columns: sortAndFilterableColumns,
    initialSorting,
    persistenceKey: 'rdAccountingQre',
  });
  const columnVisibilityState = useColumnVisibility({
    columns: sortAndFilterableColumns,
    alwaysVisibleColumns: [
      AccountingQreSortFields.Name,
      AccountingQreSortFields.QualifiedAmountCents,
      AccountingQreSortFields.AmountCents,
      AccountingQreSortFields.QreRdPercentage,
    ],
    initialVisibleColumns: [
      AccountingQreSortFields.Department,
      AccountingQreSortFields.Country,
      AccountingQreSortFields.State,
      AccountingQreSortFields.RdCategory,
    ],
    persistenceKey: 'rdAccountingQre',
  });

  const sorting = useMemo(
    () => sortState.sorts.map((s) => ({ field: s.column, direction: s.direction })),
    [sortState.sorts],
  );

  const { accountingQres, isLoadingMore, loading, onLoadMoreItems } = useAccountingQres({
    searchKeyword: searchKeywordState.searchKeyword,
    filterConditions: filterState.conditions,
    sorting,
  });

  const { accountingQreSummary, loading: loadingSummary } = useAccountingQreSummary({
    filterConditions: filterState.conditions,
    searchKeyword: searchKeywordState.searchKeyword,
  });

  const companyId = useCurrentCompanyId();

  const {
    disableApplyRDPercentageToExpensesButton,
    hasRdEligibleProjects,
    isApplyRDPercentageModalOpen,
    rdProjects,
    selectedRowIds,
    setIsApplyRDPercentageModalOpen,
    setSelectedRows,
    submit,
  } = useRdPercentageBulkApply({ fiscalYearId, loading });

  const rdAlert = rdProjects && !hasRdEligibleProjects && (
    <Alert
      severity="warning"
      title="There are no eligible R&D projects. Go to Projects page to edit project eligibility."
      actions={[
        {
          label: 'Go To Projects',
          as: NavLinkTo,
          to: getPath({ path: Path.RD_PROJECTS, companyId, fiscalYearId }),
        },
      ]}
    />
  );

  const toolbarActions = (
    <ApplyRDPercentageToExpensesButton
      size="base"
      variant="primary"
      rounded="regular"
      disabled={disableApplyRDPercentageToExpensesButton}
      id={analyticIds.setAccountingRdPercentageBtnId}
      onClick={(): void => setIsApplyRDPercentageModalOpen(true)}
    >
      Set R&D %
    </ApplyRDPercentageToExpensesButton>
  );

  return (
    <>
      <ExpenseLayout
        pageSubtitle="Select an expense to edit."
        pageTitle="Other QREs"
        connectableProviderTypes={new Set([ProviderType.Accounting])}
        connectProviderLabel="Connect Accounting"
        bulkEditProviderType={ProviderType.Accounting}
        searchKeywordState={searchKeywordState}
        filterState={filterState}
        sortState={sortState}
        columnVisibilityState={columnVisibilityState}
        createColumns={accountingQreTableColumns(
          accountingQreSummary,
          limitAccessToFinancialData,
          {
            toggle,
            setSelectedRdExpenseId,
          },
          columnVisibilityState.visibleColumns,
        )}
        tableRows={accountingQres}
        loadingRows={loading}
        loadingFooter={loadingSummary || isLoadingMore}
        onLoadMoreItems={onLoadMoreItems}
        ExpenseDetail={AccountingQreDetail}
        isPiiRedacted={false}
        LockPageLayout={RdLockPageLayout}
        fiscalYear={fiscalYear}
        fiscalYearId={fiscalYearId}
        isNowPastPeriodEndDate={isNowPastPeriodEndDate}
        toolbarActions={toolbarActions}
        onRowSelectionChange={setSelectedRows}
        analyticIds={analyticIds}
        bulkEditTemplate={{
          onDownload: downloadBulkEditTemplate,
          isDownloading: isDownloadingBulkEditTemplate,
        }}
        bulkEditUpload={bulkEditUpload}
        alert={rdAlert}
      />

      {selectedRdExpenseId && (
        <EditQreExpenseAllocationModalLoader
          isOpen={isOpen}
          close={(): void => setIsOpen(false)}
          rdExpenseId={selectedRdExpenseId}
        />
      )}
      <BulkAllocationModal
        isOpen={isApplyRDPercentageModalOpen}
        loading={loading}
        setIsOpen={setIsApplyRDPercentageModalOpen}
        submit={submit}
        headerText={`Set R&D % for ${selectedRowIds.length} ${selectedRowIds.length === 1 ? 'Expense' : 'Expenses'}`}
        sliderLabelText="R&D %"
        copy={
          <Stack gap={6} paddingY={6} direction="column">
            <Typography as="p" fontSize="sm" className="text-gray-500" textAlign="left">
              Enter the target R&D percentage. This will replace existing project allocations for the selected expenses.
            </Typography>
            <Typography as="p" fontSize="sm" className="text-gray-500" textAlign="left">
              The R&D amount will be distributed proportionally across qualified R&D projects based on their current
              share of qualified employee expenses.
            </Typography>
          </Stack>
        }
        cancelButtonId={AnalyticIdentifier.ACCOUNTING_QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_CANCEL_BTN}
        submitButtonId={AnalyticIdentifier.ACCOUNTING_QRE_LAYOUT_BULK_APPLY_RD_PERCENTAGE_SUBMIT_BTN}
      />
    </>
  );
};
